import React, { useState } from 'react';
import {
    Box,
    Typography,
    Button,
    Grid,
    Card,
    CardContent,
    Alert,
    Divider
} from '@mui/material';
import { AccountBalance, Assignment, Timeline, PlayArrow } from '@mui/icons-material';
import AperturaCajaDirecta from './AperturaCajaDirecta';
import MovimientosExtraordinariosModal from './MovimientosExtraordinariosModal';
import CierreCajaModal from './CierreCajaModal';
import CajaStatusAlert from '../components/CajaStatusAlert';
import { useSucursal } from '../contexts/SucursalContext';

/**
 * Página principal para la gestión de cajas
 * Reemplaza la funcionalidad del menú "Iniciar Caja"
 */
const GestionCajas = () => {
    const [showApertura, setShowApertura] = useState(false);
    const [showMovimientos, setShowMovimientos] = useState(false);
    const [showCierre, setShowCierre] = useState(false);
    const { cajaAbierta, cajaAbiertaDatos } = useSucursal();

    const handleIniciarCaja = () => {
        setShowApertura(true);
    };

    const handleMovimientos = () => {
        setShowMovimientos(true);
    };

    const handleCerrarCaja = () => {
        setShowCierre(true);
    };

    const acciones = [
        {
            titulo: 'Iniciar Caja',
            descripcion: 'Abrir caja con conteo físico de denominaciones',
            icono: <AccountBalance color="primary" />,
            accion: handleIniciarCaja,
            habilitado: !cajaAbierta,
            color: 'primary'
        },        {
            titulo: 'Movimientos Extraordinarios',
            descripcion: 'Registrar ingresos y egresos especiales',
            icono: <Assignment color="secondary" />,
            accion: handleMovimientos,
            habilitado: cajaAbierta,
            color: 'secondary'
        },
        {
            titulo: 'Cerrar Caja',
            descripcion: 'Realizar arqueo y cierre de caja',
            icono: <Timeline color="error" />,
            accion: handleCerrarCaja,
            habilitado: cajaAbierta,
            color: 'error'
        }
    ];

    return (
        <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
            <Typography variant="h4" gutterBottom color="primary">
                🏦 Gestión de Cajas
            </Typography>
            
            <Typography variant="subtitle1" sx={{ mb: 3, color: 'text.secondary' }}>
                Sistema completo de manejo de cajas con conteo de denominaciones
            </Typography>

            <Divider sx={{ mb: 3 }} />

            {/* Estado actual de la caja */}
            <CajaStatusAlert showActions={false} />

            {/* Información del proceso */}
            <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                    🎯 Nuevo Proceso Simplificado
                </Typography>
                <Typography variant="body2">
                    La apertura de caja ahora se realiza directamente con el conteo físico de denominaciones. 
                    El valor total del conteo será automáticamente el valor de inicio de la caja.
                </Typography>
            </Alert>

            {/* Acciones disponibles */}
            <Grid container spacing={3}>
                {acciones.map((accion, index) => (
                    <Grid item xs={12} md={4} key={index}>
                        <Card 
                            sx={{ 
                                height: '100%',
                                opacity: accion.habilitado ? 1 : 0.6,
                                cursor: accion.habilitado ? 'pointer' : 'not-allowed'
                            }}
                            onClick={accion.habilitado ? accion.accion : undefined}
                        >
                            <CardContent>
                                <Box display="flex" flexDirection="column" alignItems="center" textAlign="center">
                                    <Box sx={{ mb: 2, fontSize: 48 }}>
                                        {accion.icono}
                                    </Box>
                                    <Typography variant="h6" gutterBottom>
                                        {accion.titulo}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                        {accion.descripcion}
                                    </Typography>
                                    <Button
                                        variant={accion.habilitado ? "contained" : "outlined"}
                                        color={accion.color}
                                        disabled={!accion.habilitado}
                                        startIcon={<PlayArrow />}
                                        onClick={accion.habilitado ? accion.accion : undefined}
                                    >
                                        {accion.habilitado ? 'Ejecutar' : 'No Disponible'}
                                    </Button>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {/* Información de ayuda */}
            <Card sx={{ mt: 4, backgroundColor: 'background.default' }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        📋 Flujo de Operación
                    </Typography>
                    <Typography variant="body2" component="div">
                        <strong>1. Iniciar Caja:</strong> Ingrese las cantidades físicas de billetes y monedas. 
                        El sistema calculará automáticamente el total como valor de inicio.<br />
                        
                        <strong>2. Operaciones Diarias:</strong> Realice ventas, movimientos extraordinarios 
                        y otras operaciones normales del día.<br />
                        
                        <strong>3. Cerrar Caja:</strong> Al final del día, realice el conteo final y 
                        complete el arqueo para cerrar la caja.
                    </Typography>
                </CardContent>
            </Card>

            {/* Estadísticas rápidas */}
            {cajaAbierta && (
                <Alert severity="success" sx={{ mt: 3 }}>
                    <Typography variant="body2">
                        <strong>Caja Activa:</strong> ID {cajaAbiertaDatos} | 
                        <strong> Estado:</strong> Operativa | 
                        <strong> Acciones:</strong> Todas las operaciones habilitadas
                    </Typography>
                </Alert>
            )}            {/* Modal de apertura */}
            <AperturaCajaDirecta
                open={showApertura}
                onClose={() => setShowApertura(false)}
            />            {/* Modal de movimientos extraordinarios */}
            <MovimientosExtraordinariosModal
                open={showMovimientos}
                onClose={() => setShowMovimientos(false)}
            />

            {/* Modal de cierre de caja */}
            <CierreCajaModal
                open={showCierre}
                onClose={() => setShowCierre(false)}
            />
        </Box>
    );
};

export default GestionCajas;
